// Tripwire only: agents/users can override via `wayai unbind`.
//
// Binds a git checkout (main or linked worktree) to a single hub so push/pull
// refuse to run against a different hub once bound. NOT a concurrency lock — a
// per-worktree routing guard against a prompt landing in the wrong checkout.

import { execFileSync } from 'node:child_process';
import * as fs from 'node:fs';
import * as path from 'node:path';
import { UUID_RE } from './utils.js';

const BINDING_FILENAME = 'wayai-binding';
// Legacy filename (pre-rename, when this was called a "lock"). Still read as a
// fallback and cleared on write/clear so a checkout bound before the rename
// keeps its binding across the upgrade.
const LEGACY_BINDING_FILENAME = 'wayai-lock';

// Cache keyed by cwd so tests that chdir between checkouts re-resolve.
let _gitDirCache: { cwd: string; gitDir: string | null } | undefined;

function resolveGitDir(): string | null {
  const cwd = process.cwd();
  if (_gitDirCache && _gitDirCache.cwd === cwd) return _gitDirCache.gitDir;

  let gitDir: string | null;
  try {
    const raw = execFileSync('git', ['rev-parse', '--git-dir'], {
      cwd,
      encoding: 'utf-8',
      stdio: ['pipe', 'pipe', 'pipe'],
    }).trim();
    gitDir = raw ? path.resolve(cwd, raw) : null;
  } catch {
    gitDir = null;
  }
  _gitDirCache = { cwd, gitDir };
  return gitDir;
}

export function getBindingPath(): string | null {
  const gitDir = resolveGitDir();
  return gitDir ? path.join(gitDir, BINDING_FILENAME) : null;
}

function getLegacyBindingPath(): string | null {
  const gitDir = resolveGitDir();
  return gitDir ? path.join(gitDir, LEGACY_BINDING_FILENAME) : null;
}

// Surfaces non-ENOENT errors (EACCES) so unreadable bindings don't masquerade
// as unbound. Prefers the new filename; falls back to the legacy one so a
// checkout bound before the rename stays bound. A present-but-corrupt primary
// file wins (returns null = unbound) and does NOT fall through to the legacy.
export function readBinding(): string | null {
  for (const bindingPath of [getBindingPath(), getLegacyBindingPath()]) {
    if (!bindingPath) continue;
    let raw: string;
    try {
      raw = fs.readFileSync(bindingPath, 'utf-8');
    } catch (err) {
      if ((err as NodeJS.ErrnoException).code === 'ENOENT') continue;
      throw err;
    }
    const trimmed = raw.trim();
    return UUID_RE.test(trimmed) ? trimmed : null;
  }
  return null;
}

export function writeBinding(hubId: string): void {
  if (!UUID_RE.test(hubId)) {
    throw new Error(`Invalid hub_id (must be a UUID): ${hubId}`);
  }
  const bindingPath = getBindingPath();
  if (!bindingPath) {
    throw new Error('Not inside a git repository — cannot write hub binding.');
  }
  fs.writeFileSync(bindingPath, `${hubId}\n`, 'utf-8');
  // Migrate off the legacy filename so the two names can't drift (best-effort).
  removeLegacyBinding();
}

// Truly best-effort: a leftover legacy file is harmless (readBinding prefers
// the new filename), so failure to remove it must NOT fail a writeBinding that
// already persisted the new binding. Swallows every error, not just ENOENT.
function removeLegacyBinding(): void {
  const legacyPath = getLegacyBindingPath();
  if (!legacyPath) return;
  try {
    fs.unlinkSync(legacyPath);
  } catch {
    // ignore — migration cleanup is optional
  }
}

// Returns true if a binding existed and was removed, false if absent. Removes
// both the new and legacy files. Surfaces non-ENOENT errors.
export function clearBinding(): boolean {
  let removed = false;
  for (const bindingPath of [getBindingPath(), getLegacyBindingPath()]) {
    if (!bindingPath) continue;
    try {
      fs.unlinkSync(bindingPath);
      removed = true;
    } catch (err) {
      if ((err as NodeJS.ErrnoException).code !== 'ENOENT') throw err;
    }
  }
  return removed;
}

export function assertHubMatchesBinding(resolvedHubId: string, hubLabel?: string): void {
  const bound = readBinding();
  if (!bound) return;
  if (bound === resolvedHubId) return;

  const target = hubLabel ? `${hubLabel} (${resolvedHubId})` : resolvedHubId;
  console.error(
    [
      `This worktree is bound to hub ${bound}, but the command resolved to ${target}.`,
      '',
      'This usually means a prompt was routed to the wrong worktree.',
      'Confirm with the user before unbinding. To override:',
      '  wayai unbind             # clear the binding for this worktree',
      `  wayai use ${resolvedHubId}  # rebind this worktree to the new hub`,
    ].join('\n'),
  );
  process.exit(1);
}

// Best-effort: errors don't fail the parent operation (which already succeeded).
export function autoBindIfUnbound(hubId: string): boolean {
  try {
    if (readBinding()) return false;
    writeBinding(hubId);
    console.log(`Worktree bound to hub ${hubId} (run \`wayai unbind\` to clear).`);
    return true;
  } catch (err) {
    console.warn(`  Warning: could not auto-bind worktree (${err instanceof Error ? err.message : String(err)})`);
    return false;
  }
}

// Fail-secure: readBinding errors propagate (matches assertHubMatchesBinding).
export function assertNoBindingBlocksHubCreation(newHubName: string): void {
  const bound = readBinding();
  if (!bound) return;

  console.error(
    [
      `This worktree is bound to hub ${bound}, but you're about to create a new hub "${newHubName}" here.`,
      '',
      'This usually means a prompt was routed to the wrong worktree.',
      'Confirm with the user before unbinding. To override:',
      '  wayai unbind             # clear the binding for this worktree',
    ].join('\n'),
  );
  process.exit(1);
}
